import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Search } from 'lucide-react'
import { toast } from 'sonner'

import { affiliates, type AffiliateListItem } from '../api/endpoints'
import { Button } from '../components/Button'
import { DataTable } from '../components/DataTable'
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input } from '../components/Input'
import { AffiliateStatusBadge } from '../components/StatusBadge'
import { formatDate, formatINR } from '../lib/format'

export function AffiliatesPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<AffiliateListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  function reload() {
    setLoading(true)
    affiliates
      .list({ search: search || undefined })
      .then((r) => setData(r.affiliates))
      .catch((err) => toast.error('Failed to load affiliates', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [search])

  const columns: ColumnDef<AffiliateListItem>[] = [
    {
      header: 'Affiliate',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.original.name ?? '—'}</p>
          <p className="text-xs text-slatey">{row.original.email ?? row.original.phone ?? '—'}</p>
        </div>
      ),
    },
    {
      header: 'Code',
      accessorKey: 'referralCode',
      cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
    },
    {
      header: 'Rate',
      accessorKey: 'commissionRate',
      cell: ({ getValue }) => `${getValue<number>()}%`,
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => <AffiliateStatusBadge status={getValue<'active' | 'suspended' | 'banned'>()} />,
    },
    {
      header: 'Earned',
      accessorKey: 'totalEarned',
      cell: ({ getValue }) => formatINR(getValue<number>()),
    },
    {
      header: 'Pending',
      accessorKey: 'pendingPayout',
      cell: ({ row }) => (
        <span className={row.original.pendingPayout > 0 ? 'text-amber font-medium' : 'text-slatey'}>
          {formatINR(row.original.pendingPayout)}
        </span>
      ),
    },
    {
      header: 'Paid',
      accessorKey: 'totalPaid',
      cell: ({ getValue }) => formatINR(getValue<number>()),
    },
    { header: 'Referrals', accessorKey: 'referralsCount' },
    { header: 'Joined', accessorKey: 'createdAt', cell: ({ getValue }) => formatDate(String(getValue())) },
  ]

  return (
    <div className="space-y-4 max-w-7xl">
      <div className="card p-4 flex flex-wrap items-center gap-3">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            setSearch(searchInput.trim())
          }}
          className="flex items-center gap-2 flex-1 min-w-[260px]"
        >
          <div className="relative flex-1">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-slatey" />
            <Input
              placeholder="Search by name, email, phone, or referral code"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Search
          </Button>
        </form>
        <CreateAffiliateDialog onCreated={reload} />
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onRowClick={(a) => navigate(`/affiliates/${a.id}`)}
        emptyTitle="No affiliates yet"
        emptyDescription="Add an affiliate to start tracking referrals and commissions."
      />
    </div>
  )
}

function CreateAffiliateDialog({ onCreated }: { onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [commissionRate, setCommissionRate] = useState('10')
  const [referralCode, setReferralCode] = useState('')
  const [saving, setSaving] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      await affiliates.create({
        phone: phone.trim(),
        name: name.trim(),
        email: email.trim() || null,
        commissionRate: Number(commissionRate) || 10,
        referralCode: referralCode.trim() || undefined,
      })
      toast.success('Affiliate created')
      setOpen(false)
      setPhone('')
      setName('')
      setEmail('')
      setCommissionRate('10')
      setReferralCode('')
      onCreated()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="size-4" />
          New affiliate
        </Button>
      </DialogTrigger>
      <DialogContent title="Create affiliate" description="An 8-character referral code is auto-generated if you leave the field blank.">
        <form onSubmit={onSubmit} className="space-y-3">
          <FormField label="Phone" required>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91XXXXXXXXXX" required />
          </FormField>
          <FormField label="Name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </FormField>
          <FormField label="Email">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Commission rate (%)">
              <Input
                type="number"
                min={0}
                max={100}
                step="0.5"
                value={commissionRate}
                onChange={(e) => setCommissionRate(e.target.value)}
              />
            </FormField>
            <FormField label="Custom referral code" hint="Optional">
              <Input
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder="AUTO"
              />
            </FormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={saving} size="sm">
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}